import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateReferralData, BrokerSearchResult } from '@/types/referral.types';
import { Lead } from '@/types/lead.types';
import { referralsService } from '@/services/referrals.service';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { BrokerSearchSelect, CommissionSplitSlider } from '@/components/referrals';

export const SendReferral: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedBroker, setSelectedBroker] = useState<BrokerSearchResult | null>(null);
  const [senderSplitPercent, setSenderSplitPercent] = useState(50);
  const [receiverSplitPercent, setReceiverSplitPercent] = useState(50);
  const [notes, setNotes] = useState('');

  // Lead search state
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadResults, setLeadResults] = useState<Lead[]>([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const leadSearchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close lead dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (leadSearchRef.current && !leadSearchRef.current.contains(event.target as Node)) {
        setShowLeadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced lead search
  const searchLeads = useCallback(async (query: string) => {
    if (query.length < 2) {
      setLeadResults([]);
      return;
    }

    try {
      setIsSearchingLeads(true);
      const response = await leadsService.getAll({ search: query }, 1, 10);
      setLeadResults(response.data?.data ?? []);
    } catch (err) {
      console.error('Error searching leads:', err);
      setLeadResults([]);
    } finally {
      setIsSearchingLeads(false);
    }
  }, []);

  // Handle lead search input change
  const handleLeadSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setLeadSearchQuery(query);
    setShowLeadDropdown(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchLeads(query);
    }, 300);
  };

  // Handle lead selection
  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead);
    setLeadSearchQuery('');
    setLeadResults([]);
    setShowLeadDropdown(false);
    setValidationErrors((prev) => ({ ...prev, lead: '' }));
  };

  // Handle clear lead
  const handleClearLead = () => {
    setSelectedLead(null);
  };

  // Handle commission split change
  const handleSplitChange = (sender: number, receiver: number) => {
    setSenderSplitPercent(sender);
    setReceiverSplitPercent(receiver);
    if (sender + receiver === 100) {
      setValidationErrors((prev) => ({ ...prev, split: '' }));
    }
  };

  // Handle broker selection
  const handleBrokerChange = (broker: BrokerSearchResult | null) => {
    setSelectedBroker(broker);
    if (broker) {
      setValidationErrors((prev) => ({ ...prev, broker: '' }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!selectedLead) {
      errors.lead = 'Please select a lead';
    }

    if (!selectedBroker) {
      errors.broker = 'Please select a broker';
    }

    if (senderSplitPercent + receiverSplitPercent !== 100) {
      errors.split = 'Commission split must equal 100%';
    }

    if (senderSplitPercent < 0 || receiverSplitPercent < 0) {
      errors.split = 'Split percentages cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedLead || !selectedBroker) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const data: CreateReferralData = {
        lead_id: selectedLead.id,
        to_broker_id: selectedBroker.id,
        sender_split_percent: senderSplitPercent,
        receiver_split_percent: receiverSplitPercent,
        notes: notes.trim() || undefined,
      };

      const response = await referralsService.create(data);
      navigate(`/referrals/${response.data.id}`);
    } catch (err: any) {
      console.error('Error creating referral:', err);
      setError(err.response?.data?.message || 'Failed to send referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/referrals')}
        className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Referrals
      </button>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Send Referral</h1>
        <p className="text-secondary-600 mt-1">
          Refer a lead to another broker and share the commission
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">Failed to send referral</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Lead Selection */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Select Lead</h3>

              {selectedLead ? (
                <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <div>
                    <p className="font-medium text-secondary-900">{selectedLead.name}</p>
                    <p className="text-sm text-secondary-500">
                      {selectedLead.phone} - #{selectedLead.lead_number}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearLead}
                    className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div ref={leadSearchRef} className="relative">
                  <Input
                    value={leadSearchQuery}
                    onChange={handleLeadSearchChange}
                    onFocus={() => setShowLeadDropdown(true)}
                    placeholder="Search leads by name, phone, or lead number..."
                    error={validationErrors.lead}
                    leftIcon={
                      isSearchingLeads ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )
                    }
                  />

                  {/* Lead Search Results Dropdown */}
                  {showLeadDropdown && leadSearchQuery.length >= 2 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {isSearchingLeads ? (
                        <div className="px-4 py-3 text-sm text-secondary-500">Searching...</div>
                      ) : leadResults.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-secondary-500">
                          No leads found matching "{leadSearchQuery}"
                        </div>
                      ) : (
                        <ul className="py-1">
                          {leadResults.map((lead) => (
                            <li key={lead.id}>
                              <button
                                type="button"
                                onClick={() => handleSelectLead(lead)}
                                className="w-full text-left px-4 py-3 hover:bg-secondary-50 transition-colors"
                              >
                                <p className="text-sm font-medium text-secondary-900">{lead.name}</p>
                                <p className="text-xs text-secondary-500">
                                  {lead.phone} - #{lead.lead_number}
                                </p>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Broker Selection */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Select Broker</h3>
              <BrokerSearchSelect
                value={selectedBroker}
                onChange={handleBrokerChange}
                placeholder="Search brokers by name, firm, or phone..."
                error={validationErrors.broker}
              />
            </Card>

            {/* Commission Split */}
            <Card>
              <CommissionSplitSlider
                senderPercent={senderSplitPercent}
                receiverPercent={receiverSplitPercent}
                onChange={handleSplitChange}
                senderLabel="Your Split"
                receiverLabel="Their Split"
                error={validationErrors.split}
              />
            </Card>

            {/* Notes */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Additional Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this referral (optional)..."
                rows={4}
                className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </Card>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/referrals')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !selectedLead || !selectedBroker}
                isLoading={isSubmitting}
                leftIcon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                }
              >
                Send Referral
              </Button>
            </div>
          </form>
        </div>

        {/* Preview Card */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Referral Preview</h3>

              {(!selectedLead && !selectedBroker) ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-3">
                    <svg className="w-6 h-6 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                  <p className="text-sm text-secondary-500">
                    Select a lead and broker to see the referral preview
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Lead Info */}
                  <div className="p-3 bg-secondary-50 rounded-lg">
                    <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">Lead</p>
                    {selectedLead ? (
                      <>
                        <p className="text-sm font-medium text-secondary-900">{selectedLead.name}</p>
                        <p className="text-xs text-secondary-500">{selectedLead.phone}</p>
                      </>
                    ) : (
                      <p className="text-sm text-secondary-400 italic">Not selected</p>
                    )}
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <svg className="w-6 h-6 text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>

                  {/* Broker Info */}
                  <div className="p-3 bg-secondary-50 rounded-lg">
                    <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">To Broker</p>
                    {selectedBroker ? (
                      <>
                        <p className="text-sm font-medium text-secondary-900">{selectedBroker.full_name}</p>
                        {selectedBroker.firm_name && (
                          <p className="text-xs text-secondary-500">{selectedBroker.firm_name}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-secondary-400 italic">Not selected</p>
                    )}
                  </div>

                  {/* Split Preview */}
                  <div className="pt-4 border-t border-secondary-200">
                    <p className="text-xs text-secondary-500 uppercase tracking-wider mb-2">Commission Split</p>
                    <div className="h-6 rounded-lg overflow-hidden flex">
                      <div
                        className="bg-primary-500 flex items-center justify-center text-white text-xs font-medium"
                        style={{ width: `${senderSplitPercent}%` }}
                      >
                        {senderSplitPercent > 20 && `${senderSplitPercent}%`}
                      </div>
                      <div
                        className="bg-emerald-500 flex items-center justify-center text-white text-xs font-medium"
                        style={{ width: `${receiverSplitPercent}%` }}
                      >
                        {receiverSplitPercent > 20 && `${receiverSplitPercent}%`}
                      </div>
                    </div>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className="text-primary-600">You: {senderSplitPercent}%</span>
                      <span className="text-emerald-600">Them: {receiverSplitPercent}%</span>
                    </div>
                  </div>

                  {/* Notes Preview */}
                  {notes.trim() && (
                    <div className="pt-4 border-t border-secondary-200">
                      <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">Notes</p>
                      <p className="text-sm text-secondary-700 whitespace-pre-wrap line-clamp-3">
                        {notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendReferral;
